import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai/react";

import { useResetAtoms } from "@/hooks/useResetAtoms";
import {
  emailForSigninAtom,
  mfaResolverAtom,
  mfaVerificationIdAtom,
  needsToCreatePasswordAtom,
  onboardingShortCodeDataAtom,
} from "@/stores/atoms";

import { AuthApi } from "../api/authApi";
import { PatientsQueryKeys } from "../queries/patientsQueries";
import { useRecordAuthEventMutation } from "./auditTrailsMutations";
import { useAssignPatientShortCodeData } from "./onboardingMutations";

export const useCheckAccountStatusMutation = () => {
  return useMutation({
    mutationFn: (email: string) => AuthApi.checkAccountStatus(email),
  });
};

export const useCheckUsernameAccountStatusMutation = () => {
  return useMutation({
    mutationFn: (username: string) =>
      AuthApi.getUsernameAccountStatus(username),
  });
};

export const useCreateUsernameAccountMutation = () => {
  return useMutation({
    mutationFn: ({
      username,
      password,
    }: {
      username: string;
      password: string;
    }) => AuthApi.createUsernameAccount(username, password),
  });
};

export const useSendSignInLinkMutation = () => {
  const setEmailForSignin = useSetAtom(emailForSigninAtom);

  return useMutation({
    mutationFn: ({ email, code }: { email: string; code: string }) =>
      AuthApi.sendSignInLink(email, code),
    onSuccess: (_data, { email }) => {
      setEmailForSignin(email);
    },
  });
};

export const useSendPasswordResetEmailMutation = () => {
  return useMutation({
    mutationFn: (email: string) => AuthApi.sendPasswordResetEmail(email),
  });
};

export const useVerifyPasswordResetCodeMutation = () => {
  return useMutation({
    mutationFn: (oobCode: string) => AuthApi.verifyPasswordResetCode(oobCode),
  });
};

export const useConfirmPasswordResetMutation = () => {
  return useMutation({
    mutationFn: ({
      oobCode,
      password,
    }: {
      oobCode: string;
      password: string;
    }) => AuthApi.confirmPasswordReset(oobCode, password),
  });
};

export const useSignOutMutation = () => {
  const queryClient = useQueryClient();
  const resetAtoms = useResetAtoms();
  const { mutateAsync: recordAuthEvent } = useRecordAuthEventMutation("LOGOUT");

  return useMutation({
    mutationFn: async () => {
      // Record auth event before signing out to ensure the idToken
      // is still valid when the request is made. This is best-effort:
      // sign-out should proceed even if recording the event fails.
      try {
        await recordAuthEvent();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_error) {
        // Intentionally ignore errors from audit logging to avoid blocking sign-out
      }
      await AuthApi.signOut();
    },
    onSuccess: () => {
      queryClient.clear();
      resetAtoms();
    },
  });
};

export const useSignInWithEmailLinkMutation = () => {
  const setEmailForSignin = useSetAtom(emailForSigninAtom);
  const setNeedsToCreatePassword = useSetAtom(needsToCreatePasswordAtom);

  return useMutation({
    mutationFn: async (email: string) => {
      const authStatus = await AuthApi.checkAccountStatus(email);

      // Prevent race condition: If they lack a password, set the atom before signing in.
      if (!authStatus.hasPasswordMethod) {
        setNeedsToCreatePassword(true);
      } else {
        setNeedsToCreatePassword(false);
      }

      await AuthApi.signInWithEmailLink(email);
    },
    onSuccess: () => {
      // Clear the email after a short delay to allow for navigation to complete
      setTimeout(() => {
        setEmailForSignin(null);
      }, 1000);
    },
    retry: false,
  });
};

export const useSignInWithEmailAndPasswordMutation = () => {
  const setEmailForSignin = useSetAtom(emailForSigninAtom);

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      AuthApi.signInWithEmailAndPassword(email, password),
    onSuccess: () => {
      setTimeout(() => {
        setEmailForSignin(null);
      }, 1000);
    },
  });
};

export const useSignInWithGoogleMutation = (shortCode?: string) => {
  const setNeedsToCreatePassword = useSetAtom(needsToCreatePasswordAtom);
  const setEmailForSignin = useSetAtom(emailForSigninAtom);
  const { mutate: assignPatientShortCodeData } =
    useAssignPatientShortCodeData();

  const { mutateAsync: recordAuthEvent } = useRecordAuthEventMutation("LOGIN");

  return useMutation({
    mutationFn: async () => {
      setNeedsToCreatePassword(false);
      return AuthApi.signInWithGoogle();
    },
    onSuccess: async () => {
      if (shortCode) {
        assignPatientShortCodeData(shortCode);
      }

      try {
        await recordAuthEvent();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_error) {
        // Intentionally ignore errors from audit logging to avoid blocking sign-in
      }

      setTimeout(() => {
        setEmailForSignin(null);
      }, 1000);
    },
    retry: false,
  });
};

export const useSignInWithAppleMutation = (shortCode?: string) => {
  const setNeedsToCreatePassword = useSetAtom(needsToCreatePasswordAtom);
  const setEmailForSignin = useSetAtom(emailForSigninAtom);
  const { mutate: assignPatientShortCodeData } =
    useAssignPatientShortCodeData();

  const { mutateAsync: recordAuthEvent } = useRecordAuthEventMutation("LOGIN");

  return useMutation({
    mutationFn: async () => {
      setNeedsToCreatePassword(false);
      return AuthApi.signInWithApple();
    },
    onSuccess: async () => {
      if (shortCode) {
        assignPatientShortCodeData(shortCode);
      }

      try {
        await recordAuthEvent();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_error) {
        // Intentionally ignore errors from audit logging to avoid blocking sign-in
      }

      setTimeout(() => {
        setEmailForSignin(null);
      }, 1000);
    },
    retry: false,
  });
};
export const useUpdatePasswordMutation = () => {
  const setNeedsToCreatePassword = useSetAtom(needsToCreatePasswordAtom);

  return useMutation({
    mutationFn: (password: string) => AuthApi.updatePassword(password),
    onSuccess: () => {
      setNeedsToCreatePassword(false);
    },
  });
};

export const useSendMfaVerificationCodeMutation = () => {
  return useMutation({
    mutationFn: (
      params: Parameters<typeof AuthApi.sendMfaVerificationCode>[0],
    ) => AuthApi.sendMfaVerificationCode(params),
  });
};

export const useEnrollMfaMutation = (shortCode: string) => {
  const setMfaVerificationId = useSetAtom(mfaVerificationIdAtom);
  const onboardingShortCodeData = useAtomValue(onboardingShortCodeDataAtom);
  const queryClient = useQueryClient();

  const { mutate: assignPatientShortCodeData } =
    useAssignPatientShortCodeData();

  return useMutation({
    mutationFn: (params: Parameters<typeof AuthApi.enrollMfa>[0]) =>
      AuthApi.enrollMfa(params),
    onSuccess: async () => {
      // The query that failed before enrolment has to run again.
      await queryClient.invalidateQueries({
        queryKey: PatientsQueryKeys.me(),
      });

      // Use the current atom value in case the code was hydrated after mutation init
      const codeToAssign = shortCode || onboardingShortCodeData?.code;
      if (codeToAssign) {
        assignPatientShortCodeData(codeToAssign);
      }

      setMfaVerificationId(null);
    },
  });
};

export const useVerifyMfaSignInMutation = (shortCode: string) => {
  const setEmailForSignin = useSetAtom(emailForSigninAtom);
  const setMfaResolver = useSetAtom(mfaResolverAtom);
  const setMfaVerificationId = useSetAtom(mfaVerificationIdAtom);
  const onboardingShortCodeData = useAtomValue(onboardingShortCodeDataAtom);
  const { mutate: assignPatientShortCodeData } =
    useAssignPatientShortCodeData();

  const { mutateAsync: recordAuthEvent } = useRecordAuthEventMutation("LOGIN");

  return useMutation({
    mutationFn: (params: Parameters<typeof AuthApi.verifyMfaSignIn>[0]) =>
      AuthApi.verifyMfaSignIn(params),
    onSuccess: async () => {
      // Clear the resolver and verification ID upon successful sign in
      setMfaResolver(null);
      setMfaVerificationId(null);

      // Use the current atom value in case the code was hydrated after mutation init
      const codeToAssign = shortCode || onboardingShortCodeData?.code;
      if (codeToAssign) {
        assignPatientShortCodeData(codeToAssign);
      }

      try {
        await recordAuthEvent();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_error) {
        // Intentionally ignore errors from audit logging to avoid blocking sign-in
      }

      setTimeout(() => {
        setEmailForSignin(null);
      }, 1000);
    },
  });
};
